import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { FileText, Upload, Calendar, Clock, CheckCircle, AlertCircle, X, Link as LinkIcon, Lock, Flag, BookOpen } from 'lucide-react'
import { Link } from 'react-router-dom'
import MarkdownContent from '../../components/MarkdownContent'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function Assignments() {
  const [assignments, setAssignments] = useState([])
  const [lockedMessage, setLockedMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, pending, submitted, graded
  const [submittingAssignment, setSubmittingAssignment] = useState(null)
  const [submissionForm, setSubmissionForm] = useState({ submission_url: '', submission_text: '' })
  const [submitting, setSubmitting] = useState(false)

  // Phase 4C: Detect capstone assignment (title contains "Capstone")
  const isCapstoneAssignment = (assignment) => {
    if (!assignment) return false
    return (assignment.title || '').toLowerCase().includes('capstone')
  }

  useEffect(() => {
    fetchAssignments()
  }, [])

  const fetchAssignments = async () => {
    try {
      const { data } = await api.get('/students/assignments')
      if (Array.isArray(data)) {
        setAssignments(data)
        setLockedMessage(null)
      } else {
        setAssignments(data.assignments || [])
        setLockedMessage(data.message || null)
      }
    } catch (error) {
      toast.error('Failed to load assignments')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitAssignment = async (e) => {
    e.preventDefault()
    if (!submissionForm.submission_url && !submissionForm.submission_text) {
      toast.error('Please provide either a file URL or text submission')
      return
    }

    setSubmitting(true)
    try {
      await api.post(`/students/assignments/${submittingAssignment.id}/submit`, submissionForm)
      toast.success('Assignment submitted successfully!')
      setSubmittingAssignment(null)
      setSubmissionForm({ submission_url: '', submission_text: '' })
      fetchAssignments()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit assignment')
    } finally {
      setSubmitting(false)
    }
  }

  const filteredAssignments = assignments.filter(assignment => {
    if (filter === 'pending') return !assignment.submission_id
    if (filter === 'submitted') return assignment.submission_id && !assignment.score
    if (filter === 'graded') return assignment.score !== null
    return true
  })

  const getStatusBadge = (assignment) => {
    if (assignment.score !== null) {
      const pct = assignment.max_score > 0
        ? Math.round((assignment.score / assignment.max_score) * 100)
        : null
      return (
        <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full flex items-center gap-1">
          <CheckCircle className="w-4 h-4" />
          {assignment.score} / {assignment.max_score}{pct !== null ? ` (${pct}%)` : ''}
        </span>
      )
    }
    if (assignment.submission_id) {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
          Submitted
        </span>
      )
    }
    const dueDate = assignment.effective_due_date
      ? new Date(assignment.effective_due_date)
      : new Date(assignment.due_date)
    const isOverdue = dueDate < new Date()
    return (
      <span className={`px-3 py-1 text-sm rounded-full flex items-center gap-1 ${
        isOverdue ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
      }`}>
        <AlertCircle className="w-4 h-4" />
        {isOverdue ? 'Overdue' : 'Pending'}
      </span>
    )
  }

  if (loading) return <LoadingSpinner label="Loading assignments" />

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">My Assignments</h1>
        </div>
      </div>

      {lockedMessage && (
        <div className="pl-4 border-l-2 border-amber-500/40 flex items-start gap-3">
          <Lock size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-sans text-sm text-amber-300 font-medium mb-1">{lockedMessage}</p>
            <p className="font-sans text-xs text-cinema-muted mb-2">
              Pay any amount towards each course&apos;s tuition to unlock assignments.
            </p>
            <Link to="/student/payments" className="cinema-btn-text text-xs">Go to Payments →</Link>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'submitted', 'graded'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-1.5 text-xs font-sans capitalize transition-all border ${
              filter === status
                ? 'bg-cinema-glow/10 text-cinema-glow border-cinema-glow/30'
                : 'cinema-glass border-white/8 text-cinema-muted hover:text-white hover:border-white/20'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {filteredAssignments.length === 0 ? (
        <div className="cinema-empty">
          <FileText size={36} className="mx-auto mb-4 text-cinema-muted/20" />
          <p className="font-sans text-sm text-cinema-muted">
            {lockedMessage ? 'Make a tuition payment to unlock assignments.' : 'No assignments found'}
          </p>
        </div>
      ) : (
        <div className="cinema-list">
          {filteredAssignments.map((assignment) => {
            const dueDate = new Date(assignment.effective_due_date || assignment.due_date)
            const isOverdue = dueDate < new Date() && !assignment.submission_id
            const scorePct = assignment.max_score > 0 ? Math.round((assignment.score / assignment.max_score) * 100) : 0
            const isCapstone = isCapstoneAssignment(assignment)
            return (
              <div
                key={assignment.id}
                className={`cinema-list-row flex-col !items-stretch py-5 ${
                  isCapstone ? 'border-cinema-gold/30 bg-cinema-gold/[0.03]' : ''
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    {isCapstone && (
                      <span className="inline-block px-2 py-0.5 text-xs font-sans font-semibold uppercase tracking-wider bg-cinema-gold/10 text-cinema-gold border border-cinema-gold/30 rounded-sm mb-2">
                        Final Capstone
                      </span>
                    )}
                    <h3 className={`font-display ${isCapstone ? 'text-base sm:text-lg text-cinema-gold' : 'text-base sm:text-lg text-white'} font-normal mb-1`}>
                      {assignment.title}
                    </h3>
                    <p className="font-sans text-sm text-cinema-muted font-medium mb-1.5">
                      {assignment.course_title}
                      {assignment.lesson_title && ` · ${assignment.lesson_title}`}
                    </p>
                    {assignment.description && (
                      <div className="max-w-[850px] mt-2">
                        <p className="cinema-label mb-2">Assignment Brief</p>
                        <MarkdownContent content={assignment.description} />
                      </div>
                    )}
                  </div>
                  {/* Status badge */}
                  {assignment.score !== null ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-sans font-medium uppercase tracking-wide bg-green-500/10 text-green-400 border border-green-500/25 rounded-sm shrink-0">
                      <CheckCircle size={13} /> {assignment.score}/{assignment.max_score} ({scorePct}%)
                    </span>
                  ) : assignment.submission_id ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-sans font-medium uppercase tracking-wide bg-blue-500/10 text-blue-400 border border-blue-500/25 rounded-sm shrink-0">
                      Submitted
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm shrink-0 ${
                      isOverdue
                        ? 'bg-red-500/10 text-red-400 border-red-500/25'
                        : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25'
                    }`}>
                      <AlertCircle size={13} /> {isOverdue ? 'Overdue' : 'Pending'}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 mb-4 text-xs sm:text-sm text-cinema-muted">
                  <span className="flex items-center gap-1.5 font-sans">
                    <Calendar size={13} /> Due: {dueDate.toLocaleDateString()}
                  </span>
                  {assignment.day_number && (
                    <span className="flex items-center gap-1.5 font-sans">
                      <Clock size={13} /> Lesson Day {assignment.day_number}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 font-sans">
                    <FileText size={13} /> {assignment.assignment_type}
                  </span>
                  <span className="flex items-center gap-1.5 font-sans">
                    Max: {assignment.max_score} pts
                  </span>
                  {assignment.file_format && (
                    <span className="flex items-center gap-1.5 font-sans">
                      Format: {assignment.file_format}
                    </span>
                  )}
                </div>

                {/* Learner-facing pre-submission checklist (trainer rubric is never exposed) */}
                {assignment.learner_checklist && (
                  <div className="mb-4 pl-3 border-l border-cinema-glow/20 max-w-[850px]">
                    <p className="cinema-label mb-2 text-cinema-glow">Before You Submit</p>
                    <MarkdownContent content={assignment.learner_checklist} />
                  </div>
                )}

                {assignment.submission_id && assignment.feedback && (
                  <div className="mb-4 pl-3.5 border-l-2 border-blue-500/40">
                    <p className="font-sans text-xs text-blue-400 uppercase tracking-wider font-semibold mb-1">Feedback</p>
                    <p className="font-sans text-sm sm:text-[15px] text-cinema-soft leading-relaxed">{assignment.feedback}</p>
                  </div>
                )}

                {assignment.score !== null && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-1 bg-white/5">
                      <div
                        className={`h-1 transition-all duration-700 ${
                          scorePct >= 75 ? 'bg-green-500' : scorePct >= 50 ? 'bg-cinema-glow' : 'bg-red-500'
                        }`}
                        style={{ width: `${scorePct}%` }}
                      />
                    </div>
                    <span className="font-mono text-xs text-cinema-muted shrink-0 font-medium">{scorePct}%</span>
                  </div>
                )}

                {/* Phase 4C: Action buttons — Submit + Open Lesson */}
                <div className="flex flex-wrap items-center gap-3">
                  {!assignment.submission_id && assignment.is_unlocked !== false && (
                    <button
                      onClick={() => setSubmittingAssignment(assignment)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 cinema-glass border border-cinema-glow/30 text-cinema-glow hover:bg-cinema-glow/10 text-xs sm:text-sm font-sans font-medium transition-all min-h-[36px]"
                    >
                      <Upload size={14} /> Submit Assignment
                    </button>
                  )}
                  {assignment.submission_id && !assignment.score && assignment.is_unlocked !== false && (
                    <button
                      onClick={() => setSubmittingAssignment(assignment)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 cinema-glass border border-white/15 text-cinema-muted hover:text-white hover:border-white/25 text-xs sm:text-sm font-sans font-medium transition-all min-h-[36px]"
                    >
                      <Upload size={14} /> Resubmit
                    </button>
                  )}
                  {assignment.course_id && assignment.lesson_id && (
                    <Link
                      to={`/student/courses/${assignment.course_id}?lesson=${assignment.lesson_id}`}
                      className="inline-flex items-center gap-1.5 font-sans text-xs text-cinema-muted hover:text-cinema-glow transition-colors font-medium min-h-[36px]"
                    >
                      <BookOpen size={13} /> Open Lesson
                    </Link>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Submission Modal */}
      {submittingAssignment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between px-6 py-5 border-b border-white/8">
              <div>
                <h2 className="font-display text-lg text-white font-light">Submit Assignment</h2>
                <p className="font-sans text-xs text-cinema-muted mt-1">{submittingAssignment.title}</p>
              </div>
              <button
                onClick={() => { setSubmittingAssignment(null); setSubmissionForm({ submission_url: '', submission_text: '' }) }}
                className="text-cinema-muted hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitAssignment} className="p-6 space-y-5">
              <div>
                <label className="cinema-label flex items-center gap-2 mb-2">
                  <LinkIcon size={12} /> Submission URL
                </label>
                <input
                  type="url"
                  placeholder="https://drive.google.com/..."
                  value={submissionForm.submission_url}
                  onChange={(e) => setSubmissionForm({ ...submissionForm, submission_url: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"
                />
                <p className="font-sans text-xs text-cinema-muted mt-1">Google Drive, Dropbox, portfolio, etc.</p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/8" />
                <span className="font-sans text-xs text-cinema-muted uppercase tracking-wider font-semibold">or</span>
                <div className="flex-1 h-px bg-white/8" />
              </div>

              <div>
                <label className="cinema-label flex items-center gap-2 mb-2">
                  <FileText size={12} /> Text Submission
                </label>
                <textarea
                  rows={5}
                  placeholder="Paste your work or write your response here..."
                  value={submissionForm.submission_text}
                  onChange={(e) => setSubmissionForm({ ...submissionForm, submission_text: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40 resize-none"
                />
              </div>

              <div className="p-4 cinema-glass border border-blue-500/20 rounded-sm">
                <p className="cinema-label mb-2">Assignment Details</p>
                <div className="font-sans text-xs text-cinema-muted space-y-1">
                  <p>Type: {submittingAssignment.assignment_type}</p>
                  <p>Max Score: {submittingAssignment.max_score} points</p>
                  <p>Due: {new Date(submittingAssignment.effective_due_date || submittingAssignment.due_date).toLocaleString()}</p>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => { setSubmittingAssignment(null); setSubmissionForm({ submission_url: '', submission_text: '' }) }}
                  className="cinema-btn-ghost text-xs px-5 py-2.5 min-h-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || (!submissionForm.submission_url && !submissionForm.submission_text)}
                  className="cinema-btn-primary text-xs px-6 py-2.5 min-h-0 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><LoadingSpinner size="sm" inline /> Submitting...</>
                  ) : (
                    <><Upload size={13} /> Submit</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
